/**
 * 国家语言切换器
 * 用于PC端和移动端的语言切换功能
 *
 * 切换逻辑（本地 vs 跨站）：
 *
 * 本地切换（country+language 在当前商店的 localization.available_countries 中）：
 *   通过 Shopify localization form（POST /localization）提交，
 *   正确设置 session cookie 后由 Shopify 自动重定向到目标市场页面。
 *
 * 跨站切换（目标属于不同 Shopify 商店）：
 *   由原生 <a> 链接直接导航到目标 URL，无需 JS 拦截。
 *   对于 EU 等多国家商店，href 中已预置 pending_country/pending_lang 参数，
 *   目标站点页面加载后自动检测并提交 localization 表单完成国家/货币切换。
 */

// 防止重复声明
if (typeof CountryLanguageSwitcher === 'undefined') {
  class CountryLanguageSwitcher {
    constructor() {
      this.init();
    }

    init() {
      this.handlePendingLocaleSwitch();
      this.initEventListeners();
    }

    /**
     * 页面加载时检测 URL 中的 pending_country / pending_lang 参数。
     * 如果存在，说明是从其他站点跳转过来的跨站切换，
     * 需要自动提交 localization 表单完成语言/国家切换。
     */
    handlePendingLocaleSwitch() {
      const params = new URLSearchParams(window.location.search);
      const pendingCountry = params.get('pending_country');
      const pendingLang = params.get('pending_lang');

      if (!pendingCountry) return;

      // 防死循环：如果已经尝试过一次，不再重复
      const retryKey = '_locale_switch_attempted';
      if (sessionStorage.getItem(retryKey)) {
        sessionStorage.removeItem(retryKey);
        this.cleanPendingParams();
        return;
      }
      sessionStorage.setItem(retryKey, '1');

      const form = document.getElementById('hidden-localization-form');
      if (!form) {
        this.cleanPendingParams();
        return;
      }

      const countryInput = form.querySelector('input[name="country_code"]');
      const localeInput = form.querySelector('input[name="locale_code"]');
      const returnInput = form.querySelector('input[name="return_to"]');

      if (countryInput && pendingCountry) {
        countryInput.value = pendingCountry;
      }
      if (localeInput && pendingLang) {
        localeInput.value = pendingLang;
      }
      if (returnInput) {
        returnInput.value = window.location.pathname;
      }

      form.submit();
    }

    /**
     * 清除 URL 中的 pending 参数（不触发页面刷新）
     */
    cleanPendingParams() {
      const url = new URL(window.location.href);
      url.searchParams.delete('pending_country');
      url.searchParams.delete('pending_lang');
      window.history.replaceState({}, '', url.toString());
    }

    initEventListeners() {
      this.initMobileLanguageSwitcher();
      this.initPCLanguageSwitcher();
    }

    initMobileLanguageSwitcher() {
      const mobileLanguageModal = document.getElementById(
        'mobile-language-modal',
      );

      if (mobileLanguageModal) {
        mobileLanguageModal.addEventListener('click', (e) => {
          const languageItem = e.target.closest('.mobile-modal-item');
          if (!languageItem) return;

          const isLocal = languageItem.getAttribute('data-is-local') === 'true';
          if (isLocal) {
            e.preventDefault();
            this.handleLocalSwitch(languageItem);
          }
          // 跨站项：不拦截，由原生 <a> 链接直接导航
        });
      }
    }

    initPCLanguageSwitcher() {
      const pcLanguageModal = document.getElementById('home-language-modal');

      if (pcLanguageModal) {
        pcLanguageModal.addEventListener('click', (e) => {
          const languageItem = e.target.closest('.home-language-modal__item');
          if (!languageItem) return;

          const isLocal = languageItem.getAttribute('data-is-local') === 'true';
          if (isLocal) {
            e.preventDefault();
            this.handleLocalSwitch(languageItem);
          }
          // 跨站项：不拦截，由原生 <a> 链接直接导航
        });
      }
    }

    handleLocalSwitch(languageItem) {
      const countryCode = languageItem.getAttribute('data-value');
      const localeCode = languageItem.getAttribute('data-locale-code');
      const targetUrl = languageItem.getAttribute('data-target-url');

      const submitted = this.submitLocalizationForm(countryCode, localeCode);
      if (!submitted) {
        window.location.href = targetUrl;
      }
    }

    /**
     * 通过 Shopify 的 localization form 切换语言/国家（仅用于本地切换）
     * @returns {boolean} 是否成功提交表单
     */
    submitLocalizationForm(countryCode, localeCode) {
      const form = document.getElementById('hidden-localization-form');
      if (!form) {
        console.error('[CountryLanguageSwitcher] Localization form not found');
        return false;
      }

      const countryInput = form.querySelector('input[name="country_code"]');
      const localeInput = form.querySelector('input[name="locale_code"]');

      if (countryInput && countryCode) {
        countryInput.value = countryCode;
      }
      if (localeInput && localeCode) {
        localeInput.value = localeCode;
      }

      form.submit();
      return true;
    }
  }

  // 初始化语言切换器（防止重复初始化）
  if (!window.countryLanguageSwitcher) {
    document.addEventListener('DOMContentLoaded', function () {
      if (!window.countryLanguageSwitcher) {
        window.countryLanguageSwitcher = new CountryLanguageSwitcher();
      }
    });
  }
}
